import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import {
  DateType,
  Role as RoleT,
  Role,
  Team as TeamT,
  Team,
  User as UserT,
} from 'api/models';
import { Project as ProjectT, ProjectPhase } from 'api/project';
import {
  AllocationCreatePayload,
  AllocationDeletePayload,
  AllocationMedium,
  AllocationUpdatePayload,
  ResourceType,
  TeamLevelAggregation,
  UserLevelAggregation,
} from 'api/resource-management/types';
import { DateRange } from '../projects-report/common/range-picker/types';
import { GroupedFilter } from '../tasks/grouped-filter-popover';
import { Minutes, WorkingDaysConfiguration } from '../user-capacity';
import { ViewPresetId } from './scheduler-config/view-presets/constants';

export enum GroupBy {
  Users = 'users',
  Projects = 'projects',
}

export enum View {
  Apps = 'apps',
  Demand = 'capacity-planning',
}
export interface AvailabilityFilter {
  startDate: Moment;
  endDate: Moment;
  availableMinutes: number;
  roles: number[];
}
export interface ResourceManagementFilter {
  filter: GroupedFilter;
  availabilityFilter?: AvailabilityFilter;
}
export interface ResourceManagementDemandFilter {
  filter: GroupedFilter;
}
export interface ResourceManagementSearchParams
  extends ResourceManagementFilter {
  viewId?: number;
}

export interface ResourceManagementDemandSearchParams {
  filter: GroupedFilter;
  range: DateRange;
}
export interface ResourceManagementHashParams {
  viewPreset: ViewPresetId;
}
export type ResourceManagmentPageParams = ResourceManagementSearchParams &
  ResourceManagementHashParams;

export type ResourceManagementDemandPageParams =
  ResourceManagementDemandSearchParams & ResourceManagementHashParams;

export interface ExtendedUserIdentifier {
  type: ResourceType.User;
  user: UserT;
  role?: RoleT;
  referenceTeam?: TeamT;
}

export interface ExtendedTeamIdentifier {
  type: ResourceType.Team;
  team: TeamT;
}

export type ExtendedResourceIdentifier =
  | ExtendedUserIdentifier
  | ExtendedTeamIdentifier;

export interface AddResourceOptions {
  highlight?: boolean;
  expand?: boolean;
}
export interface AddResourceProps {
  projectId: number;
  resource: ExtendedResourceIdentifier;
  options?: AddResourceOptions;
}

export interface ProjectUser {
  user: UserT;
  role?: Role;
  referenceTeam?: Team;
}

export interface ProjectsWithMembers extends ProjectT {
  projectMembers: ProjectUser[];
}

export interface ProjectWithTeam {
  project: ProjectT | ProjectsWithMembers;
  role?: Role;
  referenceTeam?: Team;
}

export interface UserWithProjects {
  projects: ProjectWithTeam[];
  user: UserT;
}

export type UsersGroupedByCompany = { [id: number]: number[] };
export type TeamsGroupedByCompany = { [id: number]: Team[] };

export interface UserAsResourceOptions {
  includeAssignProject: boolean;
}

export type TeamWithProject = TeamT & ProjectT;

export type TeamLite = Pick<
  TeamWithProject,
  'teamId' | 'projectId' | 'teamName' | 'roleId'
> & { startDate?: DateType; endDate?: DateType; allocatedMinutes?: number };

export type TeamWithProjectGroupedByRoleLookup = Dictionary<TeamWithProject[]>;

export interface RoleWithUsersAndTeams extends RoleT {
  projectMembers: ProjectUser[];
  teams: TeamT[];
}

export interface ProjectWithUserGroupedByRole extends ProjectT {
  teamMembersByRoles: RoleWithUsersAndTeams[];
}

export interface ProjectWithUserGroupedByRoleOptions {
  includeAssignUserOrPlaceholder: boolean;
  showTeams: boolean;
}

export interface UserAllocationSummary {
  userId: number;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  capacityMinutes: Minutes;
  forViewPreset: ViewPresetId;
}

export interface TeamAllocationSummary {
  roleId: number;
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}

export interface ProjectPhaseWithStyles extends ProjectPhase {
  groupKey: DateType;
  rowIndex: number;
  height: number;
  top: number;
}

type CreatePayloadMap = Dictionary<AllocationCreatePayload>;
type UpdatePayloadMap = Dictionary<AllocationUpdatePayload>;
type DeletePayloadMap = Dictionary<AllocationDeletePayload>;

export interface BulkActionPayloadMap {
  create: CreatePayloadMap;
  update: UpdatePayloadMap;
  delete: DeletePayloadMap;
}

export type SchedulerReference = React.MutableRefObject<any>;
export type ExcludeNonWorkingDaysParam = {
  excludeNonWorkingDays: boolean;
  workingDaysConfig: WorkingDaysConfiguration;
};
export interface Range {
  fromDate: Moment;
  toDate: Moment;
}
export interface AggregationLookup {
  [x: string]: Dictionary<UserLevelAggregation | TeamLevelAggregation>;
}

export interface ReplacementUserForTeam extends UserT {
  fullfilledMinutes: number;
  isInProject: boolean;
  utilization?: number;
  margin?: number;
  cost: number;
}

export interface AddToProjectRecordType<T> {
  key: number;
  entity: T;
  isSelected: boolean;
}

export interface AllocatedMinutes {
  [AllocationMedium.Hard]: Minutes;
  [AllocationMedium.Soft]: Minutes;
}
export interface DetailedAllocatedMinutes {
  usersAllocatedMinutes: AllocatedMinutes;
  teamsAllocatedMinutes: AllocatedMinutes;
}

export interface DetailedAllocationData {
  totalAllocatedMinutes: Minutes;
  hardAllocatedMinutes: Minutes;
  softAllocatedMinutes: Minutes;
  allocatedPercentage: number;
  availableMinutes: Minutes;
}

export interface AllocationStartAndEndDate {
  minStartDate?: string | null;
  maxEndDate?: string | null;
}

// TODO: Remove RawSummary and related types
export interface RawSummary {
  [year: number]: {
    [month: number]: {
      [weekNumber: number]: {
        [day: number]: DetailedAllocatedMinutes;
      };
    };
  };
}
export interface RawSummaryOptions<T extends RawSummary> {
  fromDate?: Moment;
  toDate?: Moment;
  existingSummary?: T;
}

export type ViewportDateRange = {
  new: { startDate: DateType; endDate: DateType };
  old?: { startDate: DateType; endDate: DateType };
};

export type FilteredUserType = {
  native: UserWithProjects[];
  partner: UserWithProjects[];
  currentUser: UserWithProjects[];
};
